import { useTranslation } from 'next-i18next';
import { PricingBreakdown, PricingBreakdownAmountType } from '@interfaces/pricingBreakdown';
import Icon from '@components/atoms/icon/icon';
import React, { useMemo } from 'react';
import clsx from 'clsx';
import { usePreferences } from '@context/preferences.context';
import { getFAQLocale } from '@helpers/localization';
import styles from './buyer-fees.module.scss';

export type BuyerFeesProps = {
  breakdown?: Omit<PricingBreakdown, 'sellerEarning'>;
  isSeller?: boolean;
  productLocation?: string;
  buyerLocation?: string;
  hasPhysicalAuthentication?: boolean;
} & React.HTMLProps<HTMLDivElement>;

const BuyerFees: React.FC<BuyerFeesProps> = ({
  breakdown,
  isSeller = false,
  buyerLocation,
  productLocation,
  hasPhysicalAuthentication = false,
}) => {
  const { t } = useTranslation();
  const isCrossBorder = !!breakdown?.buyerFees?.hasCrossBorderFees;
  const displayCrossBorderLocation = isCrossBorder && ((isSeller && buyerLocation) || (!isSeller && productLocation));
  const { locale } = usePreferences();

  const services = [
    {
      icon: 'guaranteed-delivery-default',
      title: t('BUYERFEE.INFOMODAL.USP1.TITLE', 'Guaranteed delivery'),
      description: isSeller
        ? t('BUYERFEE.INFOMODAL.USP1.SUBTITLE.SELLER', 'It ships safely, or we refund your buyers')
        : t('BUYERFEE.INFOMODAL.USP1.SUBTITLE.BUYER', 'It ships safely, or your money back'),
    },
    {
      icon: hasPhysicalAuthentication ? 'authentication-default' : 'online-inspection-default',
      title: hasPhysicalAuthentication
        ? t('BUYERFEE-INFOMODAL-USP2-PHYSICAL-TITLE', 'Physical authentication')
        : t('BUYERFEE.INFOMODAL.USP2.TITLE', 'Online inspection'),
      description: hasPhysicalAuthentication
        ? t('BUYERFEE-INFOMODAL-USP2-PHYSICAL-SUBTITLE', 'Inspected in person by our experts')
        : t('BUYERFEE.INFOMODAL.USP2.SUBTITLE', 'Our experts reviewed this listing in detail'),
    },
    {
      icon: 'relist-default',
      title: t('BUYERFEE.INFOMODAL.USP3.TITLE', 'Free relist'),
      description: isSeller
        ? t('BUYERFEE.INFOMODAL.USP3.SUBTITLE.SELLER', 'Buyers can relist within 72h of delivery')
        : t('BUYERFEE.INFOMODAL.USP3.SUBTITLE.BUYER', 'Instantly relist within 72h of delivery'),
    },
    {
      icon: 'customer-service-default',
      title: t('BUYERFEE.INFOMODAL.USP4.TITLE', 'Customer support'),
      description: t('BUYERFEE.INFOMODAL.USP4.SUBTITLE', 'Chat or email, we’re here for you'),
    },
  ];

  const breakdownList = [
    {
      type: PricingBreakdownAmountType.listingPrice,
      title: t('BUYERFEE.INFOMODAL.PRICE', 'Price'),
      amount: breakdown?.sellerPrice?.formatted,
    },
    {
      type: PricingBreakdownAmountType.buyerFee,
      title: t('BUYERFEE.INFOMODAL.FEE.FLAT', 'Buyer service fee'),
      amount: breakdown?.buyerFees.amount.formatted,
    },
    {
      type: PricingBreakdownAmountType.fullPrice,
      title: t('BUYERFEE.INFOMODAL.TOTAL', 'Total'),
      amount: breakdown?.fullPrice?.formatted,
    },
  ];

  const buyerFeesFaqLink = useMemo(
    () =>
      locale &&
      `https://faq.vestiairecollective.com/hc/${getFAQLocale(
        locale,
      )}/articles/18378951869585-What-is-the-buyer-service-fee-`,
    [locale],
  );

  return (
    <div className={styles.buyerFees}>
      {!breakdown && isSeller && (
        <h2
          className={styles.buyerFees__sellerListingTitle}
          data-cy="buyer_fees_seller_listing_title"
        >
          {t('BUYERFEE.INFOMODAL.TITLE.SELLER', 'Buyer service fee')}
        </h2>
      )}
      {breakdown && (
        <div
          className={styles.buyerFees__breakdown}
          data-cy="buyer_fees_breakdown"
        >
          <h2 className={styles.buyerFees__breakdown__title}>
            {t('BUYERFEE.INFOMODAL.BREAKDOWN.TITLE', 'Price details')}
          </h2>
          {displayCrossBorderLocation && (
            <div
              data-cy="buyerFees_cross_border_info"
              className={styles.buyerFees__breakdown__crossborder}
            >
              {isSeller && buyerLocation && (
                <>
                  {t('BUYERFEE.INFOMODAL.BUYERCOUNTRY', 'Buyer location:')}{' '}
                  <span className={styles.buyerFees__breakdown__crossborder__location}>{buyerLocation}</span>
                </>
              )}
              {!isSeller && productLocation && (
                <>
                  {t('BUYERFEE.INFOMODAL.ITEMORIGIN', 'Item origin:')}{' '}
                  <span className={styles.buyerFees__breakdown__crossborder__location}>{productLocation}</span>
                </>
              )}
            </div>
          )}
          <ul className={styles.buyerFees__breakdown__list}>
            {breakdownList.map((breakdownEntry) => {
              return (
                <li
                  key={breakdownEntry.type}
                  className={clsx(
                    styles.buyerFees__breakdown__entry,
                    `${styles[`buyerFees__breakdown__entry--${breakdownEntry.type}`]}`,
                  )}
                >
                  <div>
                    {breakdownEntry.type === PricingBreakdownAmountType.buyerFee ? (
                      <a
                        href={buyerFeesFaqLink}
                        target="_blank"
                        rel="noreferrer"
                        className={styles.buyerFees__faqLink}
                      >
                        <span className={styles.buyerFees__faqLink__text}>{breakdownEntry.title}</span>
                        <Icon
                          className={styles.buyerFees__faqLink__icon}
                          name="help-default"
                        />
                      </a>
                    ) : (
                      <>{breakdownEntry.title}</>
                    )}
                  </div>
                  <div className={styles.buyerFees__breakdown__entry__amount}>{breakdownEntry.amount}</div>
                </li>
              );
            })}
          </ul>
          {!isSeller && (
            <div
              className={styles.buyerFees__breakdown__note}
              data-cy="buyer_fees_other_costs_at_checkout"
            >
              {t('BUYERFEE.INFOMODAL.DISCLAIMER.BUYER', 'Shipping, duties and taxes will be added at checkout')}
            </div>
          )}
        </div>
      )}

      <div className={styles.buyerFees__services}>
        <h2
          className={clsx(styles.buyerFees__services__title, {
            [styles['buyerFees__services__title--sellerListing']]: !breakdown && isSeller,
          })}
        >
          {!breakdown && isSeller
            ? t('BUYERFEE.INFOMODAL.SUBTITLE.SELLER', 'This fee gives buyers')
            : t('BUYERFEE.INFOMODAL.SUBTITLE.MAIN', 'Our buyer service fee provides')}
        </h2>
        <ul className={styles.buyerFees__services__list}>
          {services.map((service) => {
            return (
              <li
                key={service.icon}
                className={styles.buyerFees__services__item}
              >
                <Icon
                  name={service.icon}
                  className={styles.buyerFees__services__item__icon}
                />
                <div>
                  <h3 className={styles.buyerFees__services__item__title}>{service.title}</h3>
                  <p className={styles.buyerFees__services__item__description}>{service.description}</p>
                </div>
              </li>
            );
          })}
        </ul>
        {!breakdown && isSeller && (
          <a
            href={buyerFeesFaqLink}
            target="_blank"
            rel="noreferrer"
            className={styles.buyerFees__listingFaqLink}
          >
            {t('BUYERFEE.INFOMODAL.BUYER_FEE_LINK.SELLER', 'Learn more about our buyer service fee')}
            <Icon
              className={styles.buyerFees__faqLink__icon}
              name="help-default"
            />
          </a>
        )}
      </div>
    </div>
  );
};

export default BuyerFees;
